import debounce from 'debounce';
import { Map, Circle, Marker } from 'vue2-google-maps';
import Debug from '@/util/debug';
import { UNIT_KM, METERS_IN_KM, METERS_IN_MI } from '@/util/constants';
import * as LocationTypes from '@/models/target-location';
import locationBounds from '@/util/location-bounds';
import icons from '@/util/icons';
import mapStyle from './mapstyle.json';

const debug = Debug('locations-map');

const colors = {
  marker: '#2a3846',
  circle: '#2a3846',
};

export default {
  dependencies: [
    'googleMapsService',
  ],
  components: {
    'gmap-map': Map,
    'gmap-circle': Circle,
    'gmap-marker': Marker,
  },
  props: {
    initial: {
      validate: value => 'center' in value
        && Number.isFinite(value.center.lat)
        && Number.isFinite(value.center.lng)
        && Number.isInteger(value.zoom),
    },
    locations: {
      validate: value => Number.isFinite(value.lat)
        && Number.isFinite(value.lat)
        && Number.isInteger(value.radius)
        && (typeof value.type === 'string'),
    },
    panelSelector: String,
    minBounds: Object, // min bound radius per location type
    unit: {
      type: String,
      default: UNIT_KM,
    },
    colors: Object,
  },
  data() {
    return {
      iconUrl: icons.getMapPin(this.colors['color-map-marker'] || colors.marker, '#fff'),
      loading: true,
      options: {
        disableDefaultUI: true,
        mapTypeControl: true,
        styles: mapStyle,
      },
      center: null,
      zoom: null,
      circleFill: this.colors['color-map-circle'] || colors.circle,
      internalLocations: [],
    };
  },
  computed: {
    mapCenter: function computeMapCenter() {
      return this.center ? this.center : this.initial.center;
    },
    mapZoom: function computeMapZoom() {
      return this.zoom ? this.zoom : this.initial.zoom;
    },
    mapCircles: function computeMapCircles() {
      const toMeters = this.unit === UNIT_KM ? METERS_IN_KM : METERS_IN_MI;
      return this.internalLocations
        .filter(l => l.type === LocationTypes.LOCATION_TYPE_ADDRESS
                     || l.type === LocationTypes.LOCATION_TYPE_CITY)
        .map(l => ({
          radius: l.radius * toMeters, // in metres
          lat: l.lat,
          lng: l.lng,
        }));
    },
    mapMarkers: function computeMapMarkers() {
      return this.internalLocations.map((l, idx) => ({
        ...l,
        icon: {
          url: this.iconUrl,
          labelOrigin: new window.google.maps.Point(25, 22),
          scaledSize: new window.google.maps.Size(50, 64),
        },
        optimized: false,
        label: {
          text: String(idx + 1),
          fontFamily: 'Open Sans',
          fontSize: '16px',
          fontWeight: '400',
        },
      }));
    },
  },
  methods: {
    getBounds() {
      const panelDomObject = window.document.querySelector(this.panelSelector);
      const panelWidth = panelDomObject.offsetLeft + panelDomObject.offsetWidth;
      const screenWidth = window.document.body.clientWidth;

      const gm = window.google.maps;

      return locationBounds(
        (lat, lng, radius) => (new gm.Circle({ center: { lat, lng }, radius })).getBounds(),
        (a, b) => ((b.extend && b.union) ? a.union(b) : a.extend(b)),
        bounds => ({
          west: bounds.getSouthWest().lng(),
          south: bounds.getSouthWest().lat(),
          east: bounds.getNorthEast().lng(),
          north: bounds.getNorthEast().lat(),
        }),
        this.internalLocations,
        this.minBounds,
        panelWidth,
        screenWidth,
        this.initial,
        this.unit,
      );
    },
    panToLocations: debounce(function debouncedPanToLocations() {
      if (this.$refs.googlemap) {
        const bounds = this.getBounds();
        debug.info('Fitting map to new bounds: %o', bounds.toJSON());
        this.$refs.googlemap.fitBounds(bounds);
      }
    }, 200),
    updateInternalLocations() {
      this.internalLocations = this.locations.map(l => ({
        type: l.type,
        lat: l.lat,
        lng: l.lng,
        radius: l.radius,
      }));
    },
  },
  watch: {
    locations: function watchLocations() {
      // This delay is needed to allow parent component to reset all needed data
      // for example radius to 0 when multiple locations appear
      // this fixes a bug in vue2-google-maps that doesn't delete map objects
      // if data is changed same tick as it's being added/removed
      // (not 100% sure if this is the issue, but it fits)
      this.$nextTick(() => {
        this.updateInternalLocations();
        this.panToLocations();
      });
    },
  },
  mounted() {
    this.googleMapsService.initialize()
      .then(() => {
        const maps = window.google.maps || {};
        this.options.mapTypeId = maps.MapTypeId.ROADMAP;
        this.options.mapTypeControlOptions = {
          mapTypeIds: [
            maps.MapTypeId.HYBRID,
            maps.MapTypeId.ROADMAP,
            maps.MapTypeId.SATELLITE,
          ],
          position: maps.ControlPosition.TOP_RIGHT,
          style: maps.MapTypeControlStyle.HORIZONTAL_BAR,
        };

        this.loading = false;

        this.$nextTick(() => {
          this.$refs.googlemap.$mapCreated.then(() => {
            this.updateInternalLocations();
            this.panToLocations();
          });
        });

        window.addEventListener('resize', this.panToLocations);
      });
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.panToLocations);
  },
};



// WEBPACK FOOTER //
// ./src/components/locations-map/LocationsMap.js